/**
 * 라이프 사이클 이벤트 등록 및 전파
 * Cordova는 애플리케이션이 백그라운드에서 재개 될 때마다 재개 이벤트를 발생시킵니다.
 * Angular 애플리케이션 범위 내에서이 이벤트를 올바르게 처리하기 위해 NgZone을 사용하여 Angular 영역 내에서 이벤트 리스너를 실행합니다.
 *
 * original code from https://medium.com/@EliaPalme/how-to-wrap-an-angular-app-with-apache-cordova-909024a25d79
 * 최신 버전에 맞게 수정함.
 */

import { Injectable, NgZone } from '@angular/core';

import { BehaviorSubject, fromEvent, Observable } from 'rxjs';
import { map, share } from 'rxjs/operators';
import { environment } from 'src/environments/environment';


/**
 * 지원되는 플랫폼 열거형
 * 주의: 이 값은 device.platform에서 반환하는 값과 일치하지 않습니다.
 */
export enum PLATFORM {
  ANDROID = 'android',
  IOS = 'ios',
}


function _window(): any {
  // return the global native browser window object
  return window;
}

@Injectable({
  providedIn: 'root'
})
export class CordovaService {
  private resume: BehaviorSubject<boolean>;
  public deviceready: Observable<any>;

  constructor(
    private zone: NgZone
  ) {
    console.log('CordovaService::constructor() cordova=', this.cordova);

    this.resume = new BehaviorSubject<boolean>(null);
    const obs = fromEvent(document, 'resume')
      .pipe(
        map(event => {
          this.zone.run(() => {
            this.onResume();
          });
        })
      );

    // deviceready 이벤트 발생 시 실행
    // Cordova가 초기화 안되면, deviceready 이벤트가 발생하지 않음. (무시됨)
    this.deviceready = new Observable((observer: any) => {
      const fn = () => {
        observer.next(null);
        observer.complete();
      };
      (document as any).addEventListener('deviceready', fn, false);
      return () => {
        (document as any).removeEventListener('deviceready', fn, false);
      };
    }).pipe(share());

    this.deviceready.subscribe();
  }

  get cordova(): any {
    return _window().cordova;
  }

  get onCordova(): boolean {
    return !!_window().cordova;
  }

  public onResume(): void {
    this.resume.next(true);
  }

  public get device(): any {
    return _window().device;
  }

  public get platform(): PLATFORM | null {
    const platform = this.device?.platform;
    return typeof platform === 'string' ? platform.toLowerCase() as PLATFORM : null;
  }

  public get isWeb(): boolean {
    return !this.cordova;
  }

  public get isApp(): boolean {
    return !!this.cordova;
  }

  public get isIOS(): boolean {
    return this.platform?.toLowerCase() === PLATFORM.IOS;
  }

  public get isAndroid(): boolean {
    return this.platform?.toLowerCase() === PLATFORM.ANDROID;
  }

  // public createZoneObservable(zone: NgZone, fn: (observer: any) => any): Observable<any> {
  //   return Observable.create((observer: any) => {
  //     fn({
  //       next: (emit: any) => {
  //         zone.run(() => {
  //           observer.next(emit)
  //         });
  //       },
  //       error: (error: any) => {
  //         zone.run(() => observer.error(error));
  //       },
  //       complete: () => {
  //         zone.run(() => observer.complete());
  //       }
  //     });
  //   });
  // }

  public exec(fnSuccess, fnError, service: string, action: string, args: Array<any>): void {
    if (!this.cordova) {
      console.error('CordovaService::exec() cordova is null');
    }
    this.cordova?.exec(fnSuccess, fnError, service, action, args);
  }



  public get diagnostic(): any {
    return this.cordova.plugins.diagnostic;
  }

  // 마이크 권한 상태 확인
  public isMicrophoneAuthorized(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.zone.run(() => {
        this.diagnostic.isMicrophoneAuthorized(
          (authorized: boolean) => {
            resolve(authorized);
          },
          (error: any) => {
            reject(error);
          }
        );
      });
    });
  }

  // 마이크 권한 요청
  public requestMicrophoneAuthorization(): Promise<string> {
    return new Promise((resolve, reject) => {
      this.zone.run(() => {
        this.diagnostic.requestMicrophoneAuthorization(
          (status: string) => {
            resolve(status);
          },
          (error: any) => {
            reject(error);
          }
        );
      });
    });
  }
}
